/**
 * Ownership helpers (I4 — permissão por propriedade em pasta e lista).
 *
 * Regra: editar/excluir pasta (folder) e lista (sprint) é permitido para
 * GESTOR ou para o CRIADOR da entidade (`createdBy`, gravado pelo backend a
 * partir de `session['id_profissional']`). Registros antigos vêm com
 * `createdBy = null` → apenas gestor pode gerenciar.
 *
 * O frontend é defense-in-depth/UX — o gate final é o backend
 * (`require_gestor_or_owner`, HTTP 200 com `status: 403` no body).
 *
 * Compartilhado entre `task-sidebar` (gates dos menus Editar/Remover) e os
 * modais `folder-modal` / `sprint-modal` (botão Remover + guard de save).
 */

/**
 * Resolve o id de profissional da sessão atual.
 *
 * O payload de login expõe o id do profissional em `id_interno` (padrão
 * dominante da plataforma: `$currentUser().id_interno === item.id_profissional`);
 * `id_profissional` cobre shapes alternativos já aceitos neste módulo
 * (mesmo fallback de `task-detail-history-panel.currentUserId`).
 *
 * @param {object|null} user - resolvido via `this.$currentUser()`.
 * @returns {number|string|null}
 */
export function resolveProfissionalId(user) {
  if (!user) return null
  return user.id_interno || user.id_profissional || null
}

/**
 * True quando o usuário pode editar/excluir a entidade (pasta ou lista):
 * gestor OU criador. `createdBy` ausente/null ⇒ só gestor (E4.3).
 *
 * @param {object} [params]
 * @param {object|null} [params.entity] - folder/sprint com `createdBy`.
 * @param {object|null} [params.user] - resolvido via `this.$currentUser()`.
 * @returns {boolean}
 */
export function canManageEntity({ entity = null, user = null } = {}) {
  if (user && user.IS_GESTOR === true) return true
  const profissionalId = resolveProfissionalId(user)
  if (profissionalId == null) return false
  const createdBy = entity && entity.createdBy != null ? entity.createdBy : null
  if (createdBy == null) return false
  return createdBy === profissionalId
}

/**
 * True quando o usuário pode ADMINISTRAR a pasta (Editar/Remover + gerenciar
 * status). Bifurca pelo estado do contêiner — mesma regra do backend
 * (`core/folder_authz.authorize_folder_write`):
 *
 * - Pasta **RESTRITA** → manda a ACL: exige `nivelEfetivo === 'admin'`; ser
 *   gestor ou criador NÃO basta (RQ-16).
 * - Pasta **ABERTA** → ownership I4 (gestor OU criador).
 *
 * Fonte única — consumida pela `task-sidebar` (menu "Editar" da árvore) e pelo
 * `folder-modal` (botão Remover, guard de save e tabela de status). Duas cópias
 * divergiriam, e divergência em gate de permissão falha para o lado errado.
 *
 * @param {object} [params]
 * @param {object|null} [params.folder] - pasta com `restrito`/`nivelEfetivo`/`createdBy`.
 * @param {object|null} [params.user] - resolvido via `this.$currentUser()`.
 * @returns {boolean}
 */
export function canManageFolder({ folder = null, user = null } = {}) {
  if (!folder) return false
  if (folder.restrito) return folder.nivelEfetivo === 'admin'
  return canManageEntity({ entity: folder, user })
}
